import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Cart() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cart, setCart] = useState({ items: [], subtotal: 0 });
  const [addrs, setAddrs] = useState([]);
  const [windows, setWindows] = useState([]);
  const [selectedAddr, setSelectedAddr] = useState("");
  const [selectedWindow, setSelectedWindow] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [smsConsent, setSmsConsent] = useState(!!user?.sms_opt_in);
  const [smsMarketingConsent, setSmsMarketingConsent] = useState(!!user?.sms_marketing_opt_in);
  const [smsPhone, setSmsPhone] = useState(user?.phone || "");

  // Auth user loads async — sync saved consent so checkout never silently revokes it.
  useEffect(() => {
    if (!user) return;
    setSmsConsent(!!user.sms_opt_in);
    setSmsMarketingConsent(!!user.sms_marketing_opt_in);
    if (user.phone) setSmsPhone(user.phone);
  }, [user]);

  const load = () => {
    if (!user) return;
    api.get("/cart").then(r => setCart(r.data)).catch(() => {});
    api.get("/addresses").then(r => {
      setAddrs(r.data);
      const def = r.data.find(a => a.is_default) || r.data[0];
      if (def) setSelectedAddr(def.id);
    }).catch(() => {});
    api.get("/delivery/windows").then(r => setWindows(r.data)).catch(() => {});
  };
  useEffect(load, [user]);

  const remove = async (pid) => {
    await api.post("/cart/remove", { product_id: pid, quantity: 1 });
    load();
  };

  const checkout = async () => {
    setErr(""); setLoading(true);
    try {
      if ((smsConsent || smsMarketingConsent) && smsPhone.trim()) {
        try { await api.patch("/auth/sms-prefs", { sms_opt_in: smsConsent, sms_marketing_opt_in: smsMarketingConsent, phone: smsPhone.trim(), source: "checkout" }); } catch { /* non-blocking */ }
      }
      const origin_url = window.location.origin;
      const payload = { origin_url };
      if (selectedAddr) payload.address_id = selectedAddr;
      if (selectedWindow) payload.delivery_window_id = selectedWindow;
      const { data } = await api.post("/cart/checkout", payload);
      window.location.href = data.url;
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErr(msg);
      if (e.response?.status === 403) setTimeout(() => nav("/pricing"), 1500);
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center" data-testid="cart-guest">
        <h1 className="font-display text-4xl">Your basket is waiting.</h1>
        <p className="mt-3 text-[var(--ff-text-secondary)]">Sign in to view items in your cart.</p>
        <Link to="/login?next=/cart" className="btn-primary mt-8 inline-flex">Sign in</Link>
      </div>
    );
  }

  const delivery = cart.shipping ?? (user?.is_member ? 0 : 9);
  const ambassadorDiscount = +(cart.ambassador_discount ?? 0);
  const discountedSubtotal = +(cart.subtotal - ambassadorDiscount).toFixed(2);
  const tax = +(discountedSubtotal * ((cart.tax_rate_pct ?? 7) / 100)).toFixed(2);
  const groups = cart.groups || [{ vendor_id: "all", vendor_name: "", items: cart.items, subtotal: cart.subtotal }];
  const total = +(discountedSubtotal + delivery + tax).toFixed(2);

  // Group windows by date for nicer display
  const windowsByDate = windows.reduce((acc, w) => {
    (acc[w.date] = acc[w.date] || []).push(w); return acc;
  }, {});

  return (
    <div className="max-w-6xl mx-auto px-6 py-12" data-testid="cart-page">
      <p className="overline">Your basket</p>
      <h1 className="font-display text-5xl mt-3">Almost there.</h1>

      {cart.items.length === 0 ? (
        <div className="mt-16 text-center" data-testid="cart-empty">
          <p className="text-[var(--ff-text-secondary)]">Your basket is empty.</p>
          <Link to="/marketplace" className="btn-primary mt-6 inline-flex">Shop marketplace</Link>
        </div>
      ) : (
        <div className="mt-10 grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2 space-y-6">
            {cart.multi_rancher && (
              <div className="card-flat p-4 border-l-4 border-l-[var(--ff-citrus)] text-sm flex gap-3" data-testid="cart-multi-rancher-notice">
                <span className="text-lg">📦</span>
                <p><strong>Your order comes from {groups.length} ranchers.</strong> {cart.multi_rancher_notice} One Farm To You checkout — each rancher packs and ships their own shipment.</p>
              </div>
            )}
            {groups.map((g, gi) => (
              <div key={g.vendor_id} className="card-flat overflow-hidden" data-testid={`cart-group-${g.vendor_id}`}>
                <div className="px-5 py-3 bg-[var(--ff-muted)] flex items-center justify-between text-xs">
                  <span className="uppercase tracking-wider">Shipment {gi + 1} · Sold &amp; shipped by <Link to={`/ranchers/${g.vendor_id}`} className="font-semibold text-[var(--ff-primary)] underline">{g.vendor_name}</Link></span>
                  <span>${g.subtotal.toFixed(2)}</span>
                </div>
                <div className="divide-y divide-[var(--ff-border)]">
                  {g.items.map(it => (
                    <div key={it.id} className="p-5 flex gap-4 items-center" data-testid={`cart-item-${it.id}`}>
                      <img src={it.image_url} alt={it.name} className="w-20 h-20 rounded-lg object-cover" />
                      <div className="flex-1">
                        <p className="font-medium">{it.name}</p>
                        <p className="text-sm text-[var(--ff-text-muted)]">Qty {it.quantity} × ${(it.unit_price ?? it.price).toFixed(2)}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">${it.line_total.toFixed(2)}</p>
                        <button onClick={() => remove(it.id)} className="text-xs underline text-[var(--ff-text-muted)] mt-1" data-testid={`cart-remove-${it.id}`}>Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Address selection */}
            <div className="card-flat p-5" data-testid="cart-address-section">
              <p className="overline mb-3">Delivery address</p>
              {addrs.length === 0 ? (
                <div>
                  <p className="text-sm text-[var(--ff-text-secondary)]">You don't have a saved address yet.</p>
                  <Link to="/account" className="btn-secondary mt-3 inline-flex" data-testid="add-address-link">Add an address</Link>
                </div>
              ) : (
                <div className="space-y-2">
                  {addrs.map(a => (
                    <label key={a.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selectedAddr === a.id ? 'border-[var(--ff-primary)] bg-[var(--ff-muted)]' : 'border-[var(--ff-border)]'}`} data-testid={`addr-radio-${a.id}`}>
                      <input type="radio" name="addr" checked={selectedAddr === a.id} onChange={() => setSelectedAddr(a.id)} className="mt-1"/>
                      <div className="text-sm">
                        <p className="font-medium">{a.label} · {a.recipient}</p>
                        <p className="text-[var(--ff-text-muted)]">{a.line1}{a.line2 ? `, ${a.line2}`:""}, {a.city}, {a.state} {a.postal_code}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Delivery window */}
            <div className="card-flat p-5" data-testid="cart-window-section">
              <p className="overline mb-3">Choose a delivery window</p>
              <select className="input-base" value={selectedWindow} onChange={e => setSelectedWindow(e.target.value)} data-testid="window-select">
                <option value="">— select a delivery window —</option>
                {Object.entries(windowsByDate).map(([date, slots]) => (
                  <optgroup key={date} label={`${slots[0].weekday}, ${new Date(date).toLocaleDateString(undefined, {month:'short', day:'numeric'})}`}>
                    {slots.map(w => <option key={w.id} value={w.id}>{w.label}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
          </div>

          <div className="card-flat p-6 h-fit" data-testid="cart-summary">
            <h3 className="font-display text-2xl">Order summary</h3>
            <dl className="mt-5 space-y-2 text-sm">
              <div className="flex justify-between"><dt>Subtotal</dt><dd>${cart.subtotal.toFixed(2)}</dd></div>
              {ambassadorDiscount > 0 && (
                <div className="flex justify-between text-emerald-700 font-medium" data-testid="cart-ambassador-discount">
                  <dt>Ambassador discount ({cart.ambassador_discount_pct ?? 10}%)</dt><dd>−${ambassadorDiscount.toFixed(2)}</dd>
                </div>
              )}
              <div className="flex justify-between"><dt>Shipping{groups.length > 1 ? ` (${groups.length} shipments)` : ""}</dt><dd>{delivery === 0 ? 'Free' : `$${delivery.toFixed(2)}`}</dd></div>
              {user?.is_member && (cart.nonmember_shipping ?? 0) > 0 && (
                <div className="flex justify-between text-xs text-[var(--ff-text-muted)]" data-testid="cart-nonmember-shipping-compare">
                  <dt>Non-members pay</dt><dd className="line-through">${cart.nonmember_shipping.toFixed(2)}</dd>
                </div>
              )}
              <div className="flex justify-between"><dt>Tax (est.)</dt><dd>${tax.toFixed(2)}</dd></div>
              <div className="flex justify-between border-t border-[var(--ff-border)] pt-3 mt-3 font-semibold text-base">
                <dt>Total</dt><dd>${total.toFixed(2)}</dd>
              </div>
            </dl>
            {!user?.is_member && (
              <p className="mt-4 text-xs text-[var(--ff-secondary)] bg-[var(--ff-citrus)]/10 p-3 rounded-lg" data-testid="cart-membership-warning">
                Non-member pricing applied (+15% and paid shipping). <Link to="/pricing" className="underline">Join to save</Link>
              </p>
            )}
            {err && <p className="mt-3 text-sm text-red-700" data-testid="cart-error">{err}</p>}
            <div className="mt-5 pt-5 border-t border-[var(--ff-border)]" data-testid="cart-sms-consent-section">
              <p className="overline mb-2">Text me order updates <span className="normal-case tracking-normal text-[var(--ff-text-muted)]">(optional)</span></p>
              <input className="input-base" placeholder="Mobile number for text updates" type="tel" value={smsPhone} onChange={e => setSmsPhone(e.target.value)} data-testid="cart-sms-phone"/>
              <label className="mt-3 flex items-start gap-3 cursor-pointer select-none" data-testid="cart-sms-consent-label">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-[var(--ff-primary)]"
                  checked={smsConsent}
                  disabled={!smsPhone.trim()}
                  onChange={e => setSmsConsent(e.target.checked)}
                  data-testid="cart-sms-consent"
                />
                <span className="text-[11px] leading-relaxed text-[var(--ff-text-secondary)]">
                  I agree to receive order &amp; account texts (delivery updates, receipts) from The Farm To You at the number above. Msg &amp; data rates may apply. Msg frequency varies. Reply HELP for help, STOP to cancel.
                </span>
              </label>
              <label className="mt-3 flex items-start gap-3 cursor-pointer select-none" data-testid="cart-sms-marketing-consent-label">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-[var(--ff-primary)]"
                  checked={smsMarketingConsent}
                  disabled={!smsPhone.trim()}
                  onChange={e => setSmsMarketingConsent(e.target.checked)}
                  data-testid="cart-sms-marketing-consent"
                />
                <span className="text-[11px] leading-relaxed text-[var(--ff-text-secondary)]">
                  I agree to receive marketing &amp; promotional texts (deals, new drops) from The Farm To You at the number above. Consent is not a condition of purchase. Msg &amp; data rates may apply. Msg frequency varies. Reply HELP for help, STOP to cancel. See our{" "}
                  <Link to="/terms#sms" target="_blank" className="underline text-[var(--ff-primary)]">Terms</Link> &amp;{" "}
                  <Link to="/privacy#sms" target="_blank" className="underline text-[var(--ff-primary)]">Privacy Policy</Link>.
                </span>
              </label>
            </div>
            <button onClick={checkout} disabled={loading} className="btn-primary w-full justify-center mt-6" data-testid="cart-checkout-btn">
              {loading ? "Redirecting…" : "Proceed to checkout"}
            </button>
            <p className="mt-3 text-[11px] text-[var(--ff-text-muted)]">Products are sold and fulfilled by independent ranchers. By checking out you agree to the <Link to="/legal/marketplace-terms" className="underline">Marketplace Terms</Link> and <Link to="/legal/shipping-policy" className="underline">Shipping Policy</Link>.</p>
          </div>
        </div>
      )}
    </div>
  );
}
